import {
	Alert,
	Box,
	Button,
	Heading,
	Icons,
	Inline,
	RelativeTime,
	Spinner,
	Stack,
	Text,
	Tooltip,
	Input,
} from "@sembark-travel/ui-base"
import { Link } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAuthUser } from "../Auth"
import {
	fetchWhatsAppInboxThreads,
	TWhatsAppThread,
	type TWhatsAppMessageStatusPayload,
	type TWhatsAppThreadActivityPayload,
} from "./store"
import { WhatsAppContactAvatar } from "./conversation/ContactAvatar"
import { WhatsAppDeliveryIndicator } from "./conversation/DeliveryIndicator"
import {
	useInboxTyping,
	type TTypingUser,
} from "./conversation/useThreadTyping"
import {
	initNotifications,
	notifyInbound,
	resetBadge,
	useNotificationMute,
} from "./notifications"
import { usePrivateChannel } from "../Websockets"
import { generatePath } from "../router-utils"

const ELLIPSIS = {
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
} as const

const PAGE_SIZE = 20

// Newest activity first, id as a stable tie-breaker — mirrors the server order
// so merged (polled) rows land in the same place.
function sortThreads(threads: TWhatsAppThread[]): TWhatsAppThread[] {
	return [...threads].sort((a, b) => {
		const at = a.latest_message_at || ""
		const bt = b.latest_message_at || ""
		if (at !== bt) {
			return at < bt ? 1 : -1
		}
		return b.id - a.id
	})
}

// Upsert `incoming` over `existing` by id, keeping the recency order.
function mergeThreads(
	existing: TWhatsAppThread[],
	incoming: TWhatsAppThread[]
): TWhatsAppThread[] {
	const byId = new Map<number, TWhatsAppThread>()
	for (const thread of existing) {
		byId.set(thread.id, thread)
	}
	for (const thread of incoming) {
		byId.set(thread.id, thread)
	}
	return sortThreads(Array.from(byId.values()))
}

// Total unread across threads, skipping the open one — its own conversation
// view is responsible for notifying, and its unread is cleared while viewed.
function sumUnreadExcept(
	threads: TWhatsAppThread[],
	exceptId: number | undefined
): number {
	return threads.reduce(
		(sum, t) => (t.id === exceptId ? sum : sum + (t.unread_count || 0)),
		0
	)
}

export function WhatsAppInboxThreadsList({
	activeThreadId,
	modelType,
	modelId,
	threadPath = (threadId) =>
		generatePath("/whatsapp/inbox/:threadId", { threadId: String(threadId) }),
}: {
	activeThreadId?: number
	// Narrows the list to the threads linked to one record, by morph key — the
	// same pair the link endpoint takes. Used by the trip's WhatsApp tab.
	modelType?: string
	modelId?: number
	// Where a row links to. The inbox and the trip tab render the same list at
	// different routes.
	threadPath?: (threadId: number) => string
}) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const tenantId = user?.tenant_id
	const currentUser = useMemo(
		() => (user ? { id: user.id, name: user.name } : null),
		[user]
	)
	const containerRef = useRef<HTMLDivElement>(null)
	const loadingRef = useRef(false)
	const prevActiveRef = useRef<number | undefined>(undefined)

	// New-inbound notification (sound + tab/favicon badge). Prime audio + the
	// visibility listener once; the toggle drives the header mute button.
	const { muted, toggle: toggleMuted } = useNotificationMute()
	useEffect(() => {
		initNotifications()
		// Landing on the inbox with the tab focused means the operator is looking —
		// clear any unread badge accumulated while they were away.
		if (!document.hidden) {
			resetBadge()
		}
	}, [])

	const [term, setTerm] = useState("")
	const [q, setQ] = useState("")
	const [items, setItems] = useState<TWhatsAppThread[]>([])
	const [nextCursor, setNextCursor] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Latest items/active id for the activity handler (which must read them
	// without re-subscribing), plus the unread baseline used to detect a *new*
	// inbound message so the ding fires only when the total actually rises.
	const itemsRef = useRef<TWhatsAppThread[]>([])
	const activeRef = useRef<number | undefined>(activeThreadId)
	const unreadTotalRef = useRef(0)
	const seededRef = useRef(false)
	useEffect(() => {
		itemsRef.current = items
	}, [items])
	useEffect(() => {
		activeRef.current = activeThreadId
	}, [activeThreadId])

	// Debounce the search box into the actual query.
	useEffect(() => {
		const timer = window.setTimeout(() => setQ(term.trim()), 350)
		return () => window.clearTimeout(timer)
	}, [term])

	const loadPage = useCallback(
		async (cursor?: string) => {
			if (loadingRef.current) {
				return
			}
			loadingRef.current = true
			setIsLoading(true)
			setError(null)
			try {
				const res = await fetchWhatsAppInboxThreads(xhr, {
					q: q || undefined,
					model_type: modelType,
					model_id: modelId,
					limit: PAGE_SIZE,
					cursor,
				})
				setItems((prev) =>
					cursor ? mergeThreads(prev, res.data) : sortThreads(res.data)
				)
				if (!cursor) {
					// Seed the unread baseline from the first page so the very next
					// activity event is measured against it (no ding on initial load).
					unreadTotalRef.current = sumUnreadExcept(res.data, activeRef.current)
					seededRef.current = true
				}
				setNextCursor(res.meta.next_cursor)
			} catch (e) {
				setError((e as Error)?.message || "Unable to load conversations")
			} finally {
				loadingRef.current = false
				setIsLoading(false)
			}
		},
		[xhr, q, modelType, modelId]
	)

	// (Re)load the first page whenever the query or the record it is scoped to
	// changes.
	useEffect(() => {
		setItems([])
		setNextCursor(null)
		loadPage(undefined)
	}, [loadPage])

	// Opening a thread marks it read on the server (see the conversation view),
	// but this list keeps its own copy and only refetches on a broadcast. Clear
	// the unread count locally for both the newly-opened thread and the one just
	// left — while a thread is open the operator reads its live messages too, so a
	// mid-view refetch that re-added the count must not resurface on leaving.
	useEffect(() => {
		const previous = prevActiveRef.current
		prevActiveRef.current = activeThreadId
		const ids = [activeThreadId, previous].filter(
			(id): id is number => typeof id === "number"
		)
		if (!ids.length) {
			return
		}
		setItems((prev) => {
			let changed = false
			const next = prev.map((t) => {
				if (ids.includes(t.id) && t.unread_count) {
					changed = true
					return { ...t, unread_count: 0 }
				}
				return t
			})
			return changed ? next : prev
		})
	}, [activeThreadId])

	// Presence is a single per-user key, so only the open conversation heartbeats
	// it (see the thread view). The list stays fresh via the broadcast below.

	// Live update: when any thread in the tenant gets activity, re-pull the first
	// page and merge it in so the newly-active conversation jumps to the top.
	usePrivateChannel<
		TWhatsAppThreadActivityPayload | TWhatsAppMessageStatusPayload
	>(
		tenantId ? `wa.tenant.${tenantId}` : null,
		[".wa.thread.activity", ".wa.message.status"],
		(payload) => {
			// A delivery tick changes the row's indicator, not its position or
			// unread count, so patch it rather than re-pulling the page.
			if (payload && "status" in payload) {
				setItems((prev) =>
					prev.map((thread) =>
						thread.id === payload.thread_id &&
						thread.latest_message?.id === payload.message_id
							? {
									...thread,
									latest_message: {
										...thread.latest_message,
										status: payload.status,
										statuses:
											payload.statuses ?? thread.latest_message.statuses,
									},
								}
							: thread
					)
				)
				return
			}
			fetchWhatsAppInboxThreads(xhr, {
				q: q || undefined,
				model_type: modelType,
				model_id: modelId,
				limit: PAGE_SIZE,
			})
				.then((res) => {
					const merged = mergeThreads(itemsRef.current, res.data)
					// A rise in tenant-wide unread (excluding the open thread, whose own
					// view handles its ding) means a new inbound message landed.
					const total = sumUnreadExcept(merged, activeRef.current)
					if (seededRef.current && total > unreadTotalRef.current) {
						notifyInbound()
					}
					unreadTotalRef.current = total
					seededRef.current = true
					setItems(merged)
				})
				.catch(() => undefined)
		}
	)

	// "typing…" per row, from the same tenant channel via Echo whispers (no extra
	// subscriptions). See useInboxTyping / useThreadTyping.
	const typingByThread = useInboxTyping(tenantId, currentUser)

	const loadMore = useCallback(() => {
		if (nextCursor) {
			loadPage(nextCursor)
		}
	}, [nextCursor, loadPage])

	// Auto-load when the list is scrolled near the bottom.
	useEffect(() => {
		const container = containerRef.current
		if (!container) {
			return
		}
		let throttle: ReturnType<typeof setTimeout> | null = null
		const onScroll = () => {
			if (throttle) {
				return
			}
			throttle = setTimeout(() => {
				const nearBottom =
					container.scrollHeight - container.scrollTop <=
					container.clientHeight + 120
				if (nearBottom && nextCursor && !loadingRef.current) {
					loadMore()
				}
				throttle = null
			}, 250)
		}
		container.addEventListener("scroll", onScroll)
		return () => {
			container.removeEventListener("scroll", onScroll)
			if (throttle) {
				clearTimeout(throttle)
			}
		}
	}, [nextCursor, loadMore])

	return (
		<Stack height="full" style={{ minHeight: 0 }}>
			<Stack padding="2" borderBottomWidth="1" bgColor="subtle" gap="1">
				<Inline alignItems="center" justifyContent="between" gap="2">
					<Heading fontSize="md">Chats</Heading>
					<Tooltip
						content={muted ? "Notification sound off" : "Notification sound on"}
					>
						<Button
							level="tertiary"
							inline
							onClick={toggleMuted}
							aria-label={
								muted
									? "Turn notification sound on"
									: "Turn notification sound off"
							}
						>
							{muted ? <Icons.SpeakerXMark /> : <Icons.SpeakerWave />}
						</Button>
					</Tooltip>
				</Inline>
				<Input
					type="search"
					value={term}
					size="sm"
					onChange={(e) => setTerm(String(e.currentTarget.value))}
					placeholder="Search by name or number"
				/>
			</Stack>
			<Box
				ref={containerRef}
				style={{ overflowY: "auto", flex: 1, minHeight: 0 }}
			>
				{error ? (
					<Box padding="3">
						<Alert status="error">{error}</Alert>
					</Box>
				) : null}
				{items.length === 0 && !isLoading && !error ? (
					<Box padding="4">
						<Text color="muted" fontSize="sm">
							No conversations yet.
						</Text>
					</Box>
				) : null}
				<Stack>
					{items.map((thread) => (
						<WhatsAppInboxThreadRow
							key={thread.id}
							thread={thread}
							isActive={thread.id === activeThreadId}
							typingUsers={typingByThread[thread.id]}
							threadPath={threadPath}
						/>
					))}
				</Stack>
				<Box padding="3">
					{nextCursor ? (
						<Button
							size="sm"
							level="secondary"
							onClick={loadMore}
							disabled={isLoading}
						>
							{isLoading ? "Loading..." : "Load more"}
						</Button>
					) : isLoading ? (
						<Spinner alignCenter />
					) : null}
				</Box>
			</Box>
		</Stack>
	)
}

function WhatsAppInboxThreadRow({
	thread,
	isActive,
	typingUsers,
	threadPath,
}: {
	thread: TWhatsAppThread
	isActive: boolean
	typingUsers?: Array<TTypingUser>
	threadPath: (threadId: number) => string
}) {
	// The open thread is shown as read (its unread pointer clears on the server
	// when opened); only inactive rows surface an unread badge.
	const isUnread = Boolean(thread.unread_count) && !isActive
	const tripLink = thread.links?.find((link) => link.type === "trips")
	const tripPlanRequestLink = thread.links?.find(
		(link) => link.type === "trip_plan_requests"
	)
	// The server says what the latest message is — text, a photo, a voice
	// note, a template — so a media message without a caption is not shown
	// as an empty thread.
	const preview =
		thread.latest_message?.preview ||
		thread.latest_message?.message?.replace(/\s+/g, " ").trim()
	// While someone else is replying, the typing hint replaces the message
	// preview so the operator sees the thread is already being handled.
	const typingLabel = inboxTypingLabel(typingUsers)

	return (
		<Link
			to={threadPath(thread.id)}
			// Carry the fresh open-time read pointer + unread count to the detail so
			// its unread divider is correct regardless of the detail cache.
			state={{
				openBoundary: thread.last_read_message_id ?? 0,
				openUnread: thread.unread_count ?? 0,
			}}
			style={{ textDecoration: "none" }}
		>
			<Box
				paddingX="2"
				paddingY="4"
				borderBottomWidth="1"
				bgColor={isActive ? "primary" : "transparent"}
				borderRightWidth="2"
				borderRightColor={isActive ? "primary_emphasis" : "transparent"}
				position="relative"
			>
				{tripLink || tripPlanRequestLink ? (
					<Box position="absolute" bottom="0" right="0" paddingRight="2">
						<Box
							fontSize="xs"
							paddingX="2"
							paddingY="px"
							bgColor="warning"
							borderXWidth="1"
							borderTopWidth="1"
							borderColor="warning"
							roundedTop="md"
						>
							{tripLink
								? `Trip #${tripLink.id}`
								: tripPlanRequestLink
									? `Lead #${tripPlanRequestLink.id}`
									: "N/A"}
						</Box>
					</Box>
				) : null}
				<Inline gap="2" alignItems="start">
					<WhatsAppContactAvatar
						name={thread.contact?.name}
						number={thread.contact?.contact_number}
					/>
					<Stack gap="1" style={{ minWidth: 0, flex: 1 }}>
						<Inline gap="2" alignItems="baseline" justifyContent="between">
							<Text fontSize="sm" style={{ ...ELLIPSIS, minWidth: 0 }}>
								{thread.contact?.name ||
									thread.contact?.contact_number ||
									"Customer"}
							</Text>
							{thread.latest_message_at ? (
								<RelativeTime
									fontSize="xs"
									color={isUnread ? "success" : "muted"}
									fontWeight={isUnread ? "semibold" : "normal"}
									timestamp={thread.latest_message_at}
								/>
							) : null}
						</Inline>
						<Inline gap="2" alignItems="center" justifyContent="between">
							{typingLabel ? (
								<Text
									fontSize="sm"
									color="success"
									style={{ ...ELLIPSIS, minWidth: 0 }}
								>
									{typingLabel}
								</Text>
							) : (
								<Inline gap="1" alignItems="center" minWidth="0">
									{/*
									 * WhatsApp's trick for telling the two apart at a
									 * glance: outbound previews carry their delivery
									 * ticks, inbound ones carry nothing.
									 */}
									{thread.latest_message?.direction === "outbound" ? (
										<Inline alignItems="center" flexShrink="0" fontSize="sm">
											<WhatsAppDeliveryIndicator
												message={thread.latest_message}
											/>
										</Inline>
									) : null}
									<Text
										fontSize="sm"
										color={isUnread ? undefined : "muted"}
										style={{ ...ELLIPSIS, minWidth: 0 }}
									>
										{preview || "No messages yet"}
									</Text>
								</Inline>
							)}
							{isUnread ? (
								<Box>
									<Box
										bgColor="success_emphasis"
										color="on_emphasis"
										paddingX="2"
										paddingY="px"
										rounded="full"
										as="span"
										fontSize="sm"
										fontWeight="semibold"
									>
										{thread.unread_count}
									</Box>
								</Box>
							) : null}
						</Inline>
					</Stack>
				</Inline>
			</Box>
		</Link>
	)
}

// The compact typing hint for a list row. Uses first names only to fit the
// narrow preview line; falls back to a count when several people type at once.
function inboxTypingLabel(users?: Array<TTypingUser>): string | null {
	if (!users || users.length === 0) {
		return null
	}
	const firstNames = users.map((user) => user.name.split(" ")[0] || user.name)
	switch (firstNames.length) {
		case 1:
			return `${firstNames[0]} is typing…`
		case 2:
			return `${firstNames[0]} and ${firstNames[1]} are typing…`
		default:
			return `${firstNames.length} people typing…`
	}
}
